import type { ExtendedRequestConfig } from "@/backend/http/api.config";
import { isHttpError } from "@/backend/http/api.config.axios";
import { currentRoute } from "@/router/navigation";
import { useGlobalErrorStore } from "@/store/globalError";

import { CancelError } from "./CancelError";
import { UnauthenticatedError } from "./UnauthenticatedError";
import { isReloading, reload } from "./reload";
import { captureException, captureMessage } from "./sentry";

const UNAUTHENTICATED_MARKER = "reason=unauthenticated";

export function handleError(error: unknown, message?: string): false | undefined {
  if (isReloading()) {
    return;
  }
  if (error instanceof TypeError) {
    // browser specific error messages, one for chrome, one for firefox
    if (
      error.message.startsWith("Failed to fetch dynamically imported module") ||
      error.message.startsWith("error loading dynamically imported module")
    ) {
      reload("dynamic-import-error");
      return;
    }
  }
  // Vite dispatches this via vite:preloadError when a stylesheet dep fails to load.
  if (error instanceof Error && error.message.startsWith("Unable to preload CSS")) {
    reload("css-preload-error");
    return;
  }
  if (error instanceof UnauthenticatedError) {
    if (window.location.search.includes(UNAUTHENTICATED_MARKER)) {
      captureMessage("Stopped unauthenticated reload cycle.");
    } else {
      window.location.search += "&" + UNAUTHENTICATED_MARKER;
    }
    return;
  }
  if (error instanceof CancelError) {
    return;
  }
  if (currentRoute().name !== "app" && currentRoute().name !== "login") {
    captureException(error, { info: { message } });
    useGlobalErrorStore().setGlobalError(error, message);
    return false;
  }
  if (handleHttpError(error, message)) {
    return;
  }
  captureException(error, { message: { message } });
}

function handleHttpError(error?: unknown, message?: string) {
  if (!isHttpError(error)) {
    return false;
  }
  const config = error.config as ExtendedRequestConfig;
  const request = {
    url: joinUrl(config.baseURL, config.url),
    timeout: config.timeout,
    headers: config.headers,
    params: config.params,
    sentAt: config.sentAt,
    retry: config.retry,
    reachable: config.reachable,
    correlationId: config.headers?.["correlation-id"],
  };
  const response = {
    data: error.response?.data,
    status: error.response?.status,
    statusText: error.response?.statusText,
    headers: (error.response?.headers || {}) as Record<string, string>,
    receivedAt: new Date().toISOString(),
    error: error.toJSON(),
  };
  captureMessage((message ? `${message}: ` : "") + error.message, { request, response });
  return true;
}

function joinUrl(base: string | undefined, path: string | undefined) {
  return (base || "").replace(/\/$/, "") + "/" + (path || "").replace(/^\//, "");
}
